import React, { useState, useEffect } from 'react';
import { getCultureGenesisUsers } from '../services/api';
import { CultureGenesisUser } from '../types/sharing';
import { Avatar, Spinner } from './ui';

interface UserSelectorProps {
  selectedUserId: string | null;
  onSelectUser: (userId: string) => void;
}

const UserSelector: React.FC<UserSelectorProps> = ({ selectedUserId, onSelectUser }) => {
  const [users, setUsers] = useState<CultureGenesisUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getCultureGenesisUsers();
        setUsers(response.users || []);
      } catch (err: any) {
        console.error('Failed to load users:', err);
        setError(err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    // Skip users without email addresses (should never happen, but defensive)
    if (!user.email || typeof user.email !== 'string') {
      console.warn('[UserSelector] Skipping user without email:', user);
      return false;
    }

    const searchLower = searchTerm.toLowerCase();
    return (
      user.displayName?.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower)
    );
  });

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    searchInput: {
      padding: '10px 12px',
      border: '1px solid var(--border-2)',
      borderRadius: 'var(--radius-md)',
      fontSize: 14,
      fontFamily: 'inherit',
      color: 'var(--fg-1)',
      backgroundColor: 'var(--cg-neutral-0)',
    },
    userList: {
      maxHeight: '300px',
      overflowY: 'auto' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem',
      border: '1px solid var(--border-1)',
      borderRadius: 'var(--radius-md)',
      padding: '0.5rem',
      backgroundColor: 'var(--cg-neutral-0)',
    },
    userItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.625rem',
      borderRadius: 'var(--radius-sm)',
      cursor: 'pointer',
      transition: 'background 0.2s',
      border: '1px solid transparent',
      backgroundColor: 'transparent',
    },
    userItemSelected: {
      backgroundColor: 'var(--accent-soft)',
    },
    userItemHover: {
      backgroundColor: 'var(--bg-hover)',
    },
    userInfo: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.125rem',
    },
    userName: {
      fontSize: '0.9375rem',
      fontWeight: 500,
      color: 'var(--fg-1)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    userEmail: {
      fontSize: '0.8125rem',
      color: 'var(--fg-2)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    radio: {
      width: '18px',
      height: '18px',
      flexShrink: 0,
      cursor: 'pointer',
    },
    emptyState: {
      padding: '2rem',
      textAlign: 'center',
      color: 'var(--fg-3)',
      fontSize: '0.9375rem',
    },
    loadingState: {
      padding: '2rem',
      display: 'flex',
      justifyContent: 'center',
    },
    errorState: {
      padding: '1rem',
      background: 'var(--danger-soft)',
      border: '1px solid var(--cg-terra-200)',
      color: 'var(--danger)',
      borderRadius: 'var(--radius-md)',
      fontSize: 13,
      fontWeight: 500,
    }
  };

  const [hoveredUserId, setHoveredUserId] = useState<string | null>(null);

  if (loading) {
    return (
      <div style={styles.loadingState}>
        <Spinner size={16} label="Loading users…" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorState}>
        {error}
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <input
        type="text"
        placeholder="🔍 Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={styles.searchInput}
        autoFocus
      />

      <div style={styles.userList}>
        {filteredUsers.length === 0 ? (
          <div style={styles.emptyState}>
            {searchTerm ? 'No users found' : 'No users available'}
          </div>
        ) : (
          filteredUsers.map(user => (
            <div
              key={user.userId}
              style={{
                ...styles.userItem,
                ...(selectedUserId === user.userId ? styles.userItemSelected : {}),
                ...(hoveredUserId === user.userId && selectedUserId !== user.userId ? styles.userItemHover : {}),
              }}
              onClick={() => onSelectUser(user.userId)}
              onMouseEnter={() => setHoveredUserId(user.userId)}
              onMouseLeave={() => setHoveredUserId(null)}
            >
              <input
                type="radio"
                checked={selectedUserId === user.userId}
                onChange={() => onSelectUser(user.userId)}
                style={styles.radio}
                onClick={(e) => e.stopPropagation()}
              />

              <Avatar
                name={user.displayName || undefined}
                email={user.email}
                src={user.photoURL || undefined}
                size={40}
              />

              <div style={styles.userInfo}>
                <div
                  style={{
                    ...styles.userName,
                    ...(selectedUserId === user.userId ? { color: 'var(--accent-soft-fg)' } : {}),
                  }}
                >
                  {user.displayName || user.email.split('@')[0]}
                </div>
                <div
                  style={{
                    ...styles.userEmail,
                    ...(selectedUserId === user.userId ? { color: 'var(--accent-soft-fg)' } : {}),
                  }}
                >
                  {user.email}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserSelector;
